// Scanning a business card (BusinessCards::CameraComponent). Once a photo is
// taken or chosen, two things start at once:
//
// - the upload, straight to Active Storage (a direct upload, with progress)
// - a first reading of its text on the phone, with tesseract.js, shown as it
//   arrives
//
// When the upload is done the form submits the photo's signed id, along with
// the text if the phone has read it by then (waiting at most READ_GRACE_MS for
// it), so the lead is filled in at once. The server reads the card again
// either way (BusinessCards::ReadJob), straightened and at full size.
//
// tesseract.js's worker, engine and English data are served by the app from
// /ocr/<version> (bin/ocr-assets), not a CDN.
import { Controller } from "@hotwired/stimulus";
import { DirectUpload } from "@rails/activestorage";
import { createWorker, OEM } from "tesseract.js";
import tesseract from "tesseract.js/package.json";

const OCR_PATH = `/ocr/${tesseract.version}`;
const LONGEST_EDGE = 1600;
const READ_GRACE_MS = 10_000;

export default class CardScanController extends Controller<HTMLFormElement> {
  static override targets = [
    "image",
    "previewText",
    "chooser",
    "preview",
    "photo",
    "uploadProgress",
    "readStatus",
    "readProgress",
    "text",
    "error",
  ];
  static override values = { directUploadUrl: String };

  declare readonly imageTarget: HTMLInputElement;
  declare readonly previewTextTarget: HTMLInputElement;
  declare readonly chooserTarget: HTMLElement;
  declare readonly previewTarget: HTMLElement;
  declare readonly photoTarget: HTMLImageElement;
  declare readonly uploadProgressTarget: HTMLProgressElement;
  declare readonly readStatusTarget: HTMLElement;
  declare readonly readProgressTarget: HTMLProgressElement;
  declare readonly textTarget: HTMLElement;
  declare readonly errorTarget: HTMLElement;
  declare readonly directUploadUrlValue: string;

  async choose(event: Event): Promise<void> {
    const file = event.target instanceof HTMLInputElement ? event.target.files?.[0] : undefined;
    if (!file) return;

    this.show(file);
    const reading = this.read(file);

    try {
      this.imageTarget.value = await this.upload(file);
    } catch (error) {
      this.fail(error instanceof Error ? error.message : String(error));
      return;
    }

    const text = await Promise.race([reading, wait(READ_GRACE_MS).then(() => null)]);
    if (text) this.previewTextTarget.value = text;
    this.element.requestSubmit();
  }

  private show(file: File): void {
    swap(this.chooserTarget, "flex", "hidden");
    this.errorTarget.classList.add("hidden");
    swap(this.previewTarget, "hidden", "flex");
    this.photoTarget.src = URL.createObjectURL(file);
  }

  private upload(file: File): Promise<string> {
    const progress = this.uploadProgressTarget;
    const delegate = {
      directUploadWillStoreFileWithXHR(xhr: XMLHttpRequest): void {
        xhr.upload.addEventListener("progress", ({ loaded, total }) => {
          progress.value = total ? (loaded / total) * 100 : 0;
        });
      },
    };

    return new Promise((resolve, reject) => {
      new DirectUpload(file, this.directUploadUrlValue, delegate).create((error, blob) => {
        if (error || !blob) reject(error ?? new Error("upload failed"));
        else {
          progress.value = 100;
          const label = progress.previousElementSibling;
          if (label instanceof HTMLElement) label.textContent = label.dataset.done ?? label.textContent;
          resolve(blob.signed_id);
        }
      });
    });
  }

  // The card's text, or null when this phone can't read it.
  private async read(file: File): Promise<string | null> {
    try {
      const worker = await createWorker("eng", OEM.LSTM_ONLY, {
        workerPath: `${OCR_PATH}/worker.min.js`,
        corePath: `${OCR_PATH}/core`,
        langPath: `${OCR_PATH}/lang`,
        workerBlobURL: false,
        logger: ({ status, progress }) => {
          if (status === "recognizing text") this.readProgressTarget.value = progress * 100;
        },
      });

      try {
        const { data } = await worker.recognize(await scaled(file));
        this.readProgressTarget.value = 100;
        this.readStatusTarget.textContent = this.readStatusTarget.dataset.done ?? "";
        // Tesseract leaves a blank line between most lines.
        const text = data.text.replace(/\n\s*\n/g, "\n").trim();
        this.textTarget.textContent = text;
        this.textTarget.classList.remove("hidden");
        return text || null;
      } finally {
        await worker.terminate();
      }
    } catch {
      this.readStatusTarget.textContent = this.readStatusTarget.dataset.failed ?? "";
      return null;
    }
  }

  private fail(message: string): void {
    swap(this.previewTarget, "flex", "hidden");
    swap(this.chooserTarget, "hidden", "flex");
    this.errorTarget.textContent = `${this.errorTarget.dataset.prefix ?? ""} ${message}`.trim();
    this.errorTarget.classList.remove("hidden");
  }
}

// The photo upright (phones record rotation rather than rotating the pixels)
// and no bigger than Tesseract needs, which keeps reading quick on a phone.
async function scaled(file: File): Promise<HTMLCanvasElement> {
  const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
  const scale = Math.min(1, LONGEST_EDGE / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  canvas.getContext("2d")?.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();
  return canvas;
}

// Flex and hidden both set display, so one replaces the other.
function swap(element: HTMLElement, from: string, to: string): void {
  element.classList.remove(from);
  element.classList.add(to);
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}
